import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { Exclude, Expose } from "class-transformer";
import { IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanTelemetry extends FloorplanProductItem<TelemetryConfiguration> {
  @Exclude()
  private readonly _telemetry: TelemetryConfiguration;

  @IsString()
  @Expose({ name: "telemetryId" })
  private readonly _telemetryId: string;

  constructor(
    floorplan: Floorplan,
    telemetry: TelemetryConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    notes?: string,
  ) {
    super(floorplan, telemetry, id, x, y, size, notes);
    this._telemetryId = telemetry?.id;
    this._telemetry = telemetry;
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.telemetriesItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanTelemetry) | undefined {
    const config = this.config;
    const size = this.size;

    return (x: number, y: number) => FloorplanTelemetry.create(this._floorplan, config, x, y, size);
  }

  override delete() {
    this._floorplan.deleteTelemetry(this);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.telemetries;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanTelemetry,
        this._floorplan.telemetries,
        FloorplanEventType.TELEMETRY_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanTelemetry) => void) | undefined,
      );
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshTelemetriesLock();
  }

  protected override getConfigFromProject(floorplan: Floorplan): TelemetryConfiguration | undefined {
    return floorplan.getTelemetryConfig(this._telemetryId);
  }

  static create(floorplan: Floorplan, telemetry: TelemetryConfiguration, x: number, y: number, size: number): FloorplanTelemetry {
    return new FloorplanTelemetry(floorplan, telemetry, uuidv4(), x, y, size);
  }
}
